import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Search, BookOpen, Trash2, Volume2, Loader2, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Sidebar } from "@/components/Sidebar";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { useVocabulary, VocabularyWord } from "@/hooks/useVocabulary";
import { supabase } from "@/integrations/supabase/client";
import { VideoPlayer } from "@/components/player/VideoPlayer";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function Vocabulary() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { toast } = useToast();
  const { vocabulary, isLoading, deleteWord } = useVocabulary();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleLogout = async () => {
    await signOut();
    toast({ title: "已退出登录" });
    navigate("/");
  };

  const handleNavigate = (page: string) => {
    if (page === "home") navigate("/");
    else if (page === "vocabulary") navigate("/vocabulary");
    else if (page === "favorites") navigate("/favorites");
    else if (page === "history") navigate("/history");
    else if (page === "feedback") navigate("/feedback");
    else if (page === "profile") navigate("/profile");
  };

  const handleDelete = async (wordId: string) => {
    setDeletingId(wordId);
    const { error } = await deleteWord(wordId);
    setDeletingId(null);
    
    if (error) {
      toast({ title: "删除失败", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "已从生词本删除" });
    }
  };

  const filteredVocabulary = vocabulary.filter(
    (word) =>
      word.word.includes(searchQuery) ||
      (word.reading && word.reading.includes(searchQuery)) ||
      (word.meaning && word.meaning.includes(searchQuery))
  );

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <BookOpen className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground mb-4">请先登录以查看生词本</p>
            <Button onClick={() => navigate("/auth")}>去登录</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar
        isCollapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        currentPage="vocabulary"
        onNavigate={handleNavigate}
        isLoggedIn={!!user}
        onLogout={handleLogout}
      />

      <main className={cn(
        "transition-all duration-300",
        sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"
      )}>
        <div className="container px-6 py-8 max-w-4xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/")}
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-foreground">生词本</h1>
                <p className="text-sm text-muted-foreground">
                  共 {vocabulary.length} 个单词
                </p>
              </div>
            </div>
          </div>

          {/* Search */}
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="搜索单词..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Word List */}
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredVocabulary.length === 0 ? (
            <Card>
              <CardContent className="pt-12 pb-12 text-center">
                <BookOpen className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">
                  {searchQuery ? "没有找到匹配的单词" : "生词本还是空的，去学习视频添加单词吧！"}
                </p>
                {!searchQuery && (
                  <Button className="mt-4" onClick={() => navigate("/")}>
                    开始学习
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {filteredVocabulary.map((word) => (
                <Card key={word.id} className="group hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1 flex-wrap">
                          <span className="text-xl font-bold text-foreground">
                            {word.word}
                          </span>
                          {word.reading && (
                            <span className="text-primary">{word.reading}</span>
                          )}
                          {word.romaji && (
                            <span className="text-sm text-muted-foreground italic">
                              {word.romaji}
                            </span>
                          )}
                          {word.part_of_speech && (
                            <span className="px-1.5 py-0.5 bg-muted rounded text-xs text-muted-foreground">
                              {word.part_of_speech}
                            </span>
                          )}
                        </div>
                        {word.meaning && (
                          <p className="text-foreground mb-2">{word.meaning}</p>
                        )}
                        {word.example_sentence && (
                          <p className="text-sm text-muted-foreground bg-muted/50 rounded-lg p-2">
                            {word.example_sentence}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8 text-destructive"
                              disabled={deletingId === word.id}
                            >
                              {deletingId === word.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>确认删除？</AlertDialogTitle>
                              <AlertDialogDescription>
                                确定要将 "{word.word}" 从生词本中移除吗？此操作无法撤销。
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>取消</AlertDialogCancel>
                              <AlertDialogAction 
                                onClick={() => handleDelete(word.id)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                删除
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
